/**
 * EPIC283-C/D/E/F sellable site shell context helpers.
 * Fail closed on invalid site context — no silent site fallback.
 * D: Trusted Now ready path via nowPresenter (never invent issue/location).
 * E: Over time via overTimePresenter (K1 windows; no invented trends).
 * F: Since control via sinceControlPresenter (descriptive change only).
 */

import {
  supportingActivityTotal,
  type SiteConditionMeasurementWindow,
  type SiteConditionState,
  type SiteConditionStateResponse,
} from '../continuousSafety/siteConditionState';
import {
  evaluateNowReadiness,
  formatNowReadinessDiagnostics,
  readinessInputFromK1Now,
  type NowReadinessInput,
  type NowReadinessResult,
  type ReadinessDiagnosticRow,
} from './nowReadiness';
import { displayStateContent, type DisplayStateKey } from './contentContract';
import {
  presentTrustedNow,
  type NowFocusInput,
} from './nowPresenter';
import {
  DEFAULT_OVER_TIME_WINDOW,
  presentOverTime,
  type OverTimePresentation,
  type OverTimeWindowId,
} from './overTimePresenter';
import {
  presentSinceControl,
  type SinceControlPresentation,
} from './sinceControlPresenter';

export {
  DEFAULT_OVER_TIME_WINDOW,
  encodeOverTimeWindowParam,
  parseOverTimeWindowId,
  type OverTimeWindowId,
} from './overTimePresenter';

export type ShellViewId = 'now' | 'over_time' | 'since_control';

export type SiteConditionSelection =
  | {
      ok: true;
      condition: SiteConditionState;
      conditionId: string;
      /** No approved customer family-name mapping exists yet. */
      customerLabel: 'Measured condition';
    }
  | {
      ok: false;
      reason: 'no_conditions' | 'invalid_condition' | 'condition_unavailable';
      customerLabel: null;
    };

export const SHELL_FLAG_STORAGE_KEY = 'sellable_site_experience_shell';
export const SHELL_FLAG_QUERY = 'sellable_shell';
export const SHELL_ROUTE_PATH = '/acceptance/site-condition';
export const SHELL_FLAG_OFF_REDIRECT = '/acceptance/site-view';

/** Deep-link into the sellable three-view shell (requires sellable_shell=1 + site_id). */
export function buildSellableShellHref(params: {
  siteId: string;
  orgId?: string | null;
  view?: ShellViewId;
}): string {
  const q = new URLSearchParams();
  const siteId = String(params.siteId || '').trim();
  if (siteId) q.set('site_id', siteId);
  q.set(SHELL_FLAG_QUERY, '1');
  if (params.view) q.set('view', params.view);
  const orgId = String(params.orgId || '').trim();
  if (orgId) q.set('org_id', orgId);
  return `${SHELL_ROUTE_PATH}?${q.toString()}`;
}

/** Compatibility: fold retired Site condition bookmarks into Overview. */
export function buildSiteConditionOverviewRedirectPath(search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const next = new URLSearchParams();
  const siteId = String(params.get('site_id') || '').trim();
  if (siteId) next.set('site_id', siteId);
  const view = String(params.get('view') || '').trim();
  if (view === 'now' || view === 'over_time' || view === 'since_control') {
    next.set('focus', view);
  }
  const qs = next.toString();
  return `/acceptance/overview${qs ? `?${qs}` : ''}`;
}

export type ShellContextResolveInput = {
  /** Explicit query/path site_id only — do not pass last-nav fallback. */
  siteIdFromQuery?: string | null;
  viewFromQuery?: string | null;
  searchParams?: URLSearchParams | null;
  envFlag?: string | null;
  localStorageFlag?: string | null;
};

export type ShellContextResolution =
  | {
      ok: true;
      siteId: string;
      view: ShellViewId;
      shellEnabled: true;
    }
  | {
      ok: false;
      reason: 'flag_off' | 'missing_site' | 'invalid_site' | 'unauthorized_context';
      shellEnabled: boolean;
      redirectTo?: string;
      message: string;
    };

const VIEW_IDS: ShellViewId[] = ['now', 'over_time', 'since_control'];

export function isSellableSiteShellEnabled(params: {
  searchParams?: URLSearchParams | null;
  envFlag?: string | null;
  localStorageFlag?: string | null;
}): boolean {
  const q = params.searchParams?.get(SHELL_FLAG_QUERY);
  if (q === '0' || q === 'false') return false;
  if (q === '1' || q === 'true') return true;
  if (params.localStorageFlag === '1' || params.localStorageFlag === 'true') return true;
  if (params.envFlag === 'true' || params.envFlag === '1') return true;
  return false;
}

export function parseShellView(raw: string | null | undefined): ShellViewId {
  if (raw && VIEW_IDS.includes(raw as ShellViewId)) {
    return raw as ShellViewId;
  }
  return 'now';
}

/**
 * Resolve shell context. Missing/blank site_id fails closed — never invent a site.
 */
export function resolveShellContext(input: ShellContextResolveInput): ShellContextResolution {
  const shellEnabled = isSellableSiteShellEnabled({
    searchParams: input.searchParams,
    envFlag: input.envFlag,
    localStorageFlag: input.localStorageFlag,
  });

  if (!shellEnabled) {
    return {
      ok: false,
      reason: 'flag_off',
      shellEnabled: false,
      redirectTo: SHELL_FLAG_OFF_REDIRECT,
      message: 'Sellable site experience shell is not enabled.',
    };
  }

  const siteId = String(input.siteIdFromQuery || '').trim();
  if (!siteId) {
    return {
      ok: false,
      reason: 'missing_site',
      shellEnabled: true,
      message:
        'Site context is required. Open this view with an explicit site_id — no silent site fallback.',
    };
  }
  if (!/^[a-zA-Z0-9._:-]+$/.test(siteId)) {
    return {
      ok: false,
      reason: 'invalid_site',
      shellEnabled: true,
      message: 'Site context is invalid. Refusing to load another site.',
    };
  }

  return {
    ok: true,
    siteId,
    view: parseShellView(input.viewFromQuery),
    shellEnabled: true,
  };
}

export type ShellViewModel = {
  view: ShellViewId;
  siteId: string;
  shiftLabel: string;
  asOfLabel: string;
  stateKey: DisplayStateKey;
  stateLabel: string;
  message: string;
  limitation: string;
  primaryCta: string;
  readiness: NowReadinessResult;
  /** True when showing readiness-unavailable instead of trusted Now. */
  nowBlockedByReadiness: boolean;
  /** True when Now shows Trusted Now (B ready). */
  showTrustedNow: boolean;
  /** Above-the-fold issue/location line (honest unavailable when K1 omits customer-safe labels). */
  issueLocationLine: string | null;
  /** Declared update-mode summary for methodology disclosure (Trusted Now only). */
  updateModeSummary: string | null;
  /** Measurement ≠ condition reminder (Trusted Now / Over time / Since control). */
  methodologySummary: string | null;
  /** Declared-field diagnostics for progressive disclosure. */
  diagnostics: ReadinessDiagnosticRow[];
  /** Polished Over time presentation (E); null on Now / Since control. */
  overTime: OverTimePresentation | null;
  /** Polished Since control presentation (F); null on Now / Over time. */
  sinceControl: SinceControlPresentation | null;
};

function readinessInputForCondition(
  data: SiteConditionStateResponse | null | undefined,
  condition: SiteConditionState | null | undefined,
): NowReadinessInput {
  const now = condition?.operationalMeasurements?.now;
  const bucket = now?.buckets?.[0];
  return readinessInputFromK1Now({
    displayState: now?.displayState || bucket?.displayState || null,
    displayAsOf: now?.displayAsOf || bucket?.displayAsOf || null,
    observedThrough: now?.observedThrough || bucket?.observedThrough || null,
    queryAsOf: data?.queryAsOf || null,
    updateMode: now?.updateMode,
    nextTrigger: now?.nextTrigger,
    nextTriggerNullReason: now?.nextTriggerNullReason,
    sourceAge: now?.sourceAge,
    stageLatency: now?.stageLatency,
  });
}

const USABLE_NOW_STATE_RANK: Readonly<Record<string, number>> = {
  current: 0,
  current_with_limitations: 1,
  current_with_limitation: 1,
  delayed: 2,
  stale: 3,
};

function normalizedDisplayState(raw: string | null | undefined): string {
  return String(raw || '').trim().toLowerCase().replace(/-/g, '_');
}

function conditionEligibilityRank(
  data: SiteConditionStateResponse,
  condition: SiteConditionState,
): number {
  const state = normalizedDisplayState(condition.operationalMeasurements.now.displayState);
  const stateRank = USABLE_NOW_STATE_RANK[state];
  if (stateRank === undefined) return 100;
  const readiness = evaluateNowReadiness(readinessInputForCondition(data, condition));
  // A usable state with an incomplete governance envelope remains ahead of
  // never-measured/unavailable, but cannot claim Trusted Now.
  return readiness.mayClaimTrustedNow ? stateRank : 10 + stateRank;
}

function canonicalConditionCompare(a: SiteConditionState, b: SiteConditionState): number {
  return (
    a.conditionId.localeCompare(b.conditionId)
    || a.conditionFamily.localeCompare(b.conditionFamily)
  );
}

/**
 * Select one condition for Now, Over time, and Since control.
 * Measurement state is only eligibility/currentness; it is never severity.
 */
export function selectSiteCondition(
  data: SiteConditionStateResponse | null | undefined,
  explicitConditionId?: string | null,
): SiteConditionSelection {
  const explicitRequested = explicitConditionId !== undefined && explicitConditionId !== null;
  const requested = String(explicitConditionId || '').trim();
  if (explicitRequested) {
    if (!requested) {
      return { ok: false, reason: 'invalid_condition', customerLabel: null };
    }
    const condition = data?.conditions?.find((item) => item.conditionId === requested);
    return condition
      ? {
          ok: true,
          condition,
          conditionId: condition.conditionId,
          customerLabel: 'Measured condition',
        }
      : { ok: false, reason: 'condition_unavailable', customerLabel: null };
  }

  if (!data?.conditions?.length) {
    return { ok: false, reason: 'no_conditions', customerLabel: null };
  }
  const condition = [...data.conditions].sort((a, b) => {
    return (
      conditionEligibilityRank(data, a) - conditionEligibilityRank(data, b)
      || canonicalConditionCompare(a, b)
    );
  })[0];
  return {
    ok: true,
    condition,
    conditionId: condition.conditionId,
    customerLabel: 'Measured condition',
  };
}

/** Extract B readiness input from the deterministically selected K1 condition. */
export function readinessInputFromSiteConditionResponse(
  data: SiteConditionStateResponse | null | undefined,
  explicitConditionId?: string | null,
): NowReadinessInput {
  const selected = selectSiteCondition(data, explicitConditionId);
  return readinessInputForCondition(data, selected.ok ? selected.condition : null);
}

/**
 * Optional customer-safe issue/location from K1.
 * Today K1 exposes condition_family / zone ids — not customer nouns.
 * Do not invent labels from those fields; return empty until the contract emits them.
 */
export function nowFocusFromSiteConditionResponse(
  data: SiteConditionStateResponse | null | undefined,
  explicitConditionId?: string | null,
): NowFocusInput {
  const selected = selectSiteCondition(data, explicitConditionId);
  const now = selected.ok ? selected.condition.operationalMeasurements.now : null;
  // Facts surface: supporting bucket activity — never claim measurementValue.
  const value = supportingActivityTotal(now);
  return {
    topIssueLabel: null,
    locationLabel: null,
    measurementValue: value != null && Number.isFinite(value) ? value : null,
    measurementLimited: now?.availability === 'limited',
  };
}

export function overTimeWindowsFromSiteConditionResponse(
  data: SiteConditionStateResponse | null | undefined,
  explicitConditionId?: string | null,
): Record<string, SiteConditionMeasurementWindow> {
  const selected = selectSiteCondition(data, explicitConditionId);
  return selected.ok ? selected.condition.operationalMeasurements.overTime : {};
}

export function sinceControlWindowFromSiteConditionResponse(
  data: SiteConditionStateResponse | null | undefined,
  explicitConditionId?: string | null,
): SiteConditionMeasurementWindow | null {
  const selected = selectSiteCondition(data, explicitConditionId);
  return selected.ok ? selected.condition.operationalMeasurements.sinceControl : null;
}

/**
 * Build shell Now/view model.
 * B-blocked Now → readiness_unavailable. B-ready Now → Trusted Now (283-D).
 * Over time → polished K1 windows (283-E).
 * Since control → polished K1 since_control (283-F).
 */
export function buildShellViewModel(params: {
  view: ShellViewId;
  siteId: string;
  /** Optional K1-shaped readiness fields when available. */
  readinessInput?: NowReadinessInput;
  /** Optional customer-safe issue/location — never invent from condition_family. */
  nowFocus?: NowFocusInput | null;
  /** K1 over_time windows (E). */
  overTimeWindows?: Record<string, SiteConditionMeasurementWindow> | null;
  /** Selected over-time window; defaults to rolling_24h (1D). */
  overTimeWindowId?: OverTimeWindowId | null;
  /** K1 since_control window (F). */
  sinceControlWindow?: SiteConditionMeasurementWindow | null;
  /** Fetch/status note for shell stubs (e.g. K1 disabled). */
  dataNote?: string | null;
}): ShellViewModel {
  const readiness = evaluateNowReadiness(params.readinessInput || {});
  const nowBlockedByReadiness = !readiness.mayClaimTrustedNow;

  if (params.view === 'now' && nowBlockedByReadiness) {
    const block = displayStateContent('readiness_unavailable');
    return {
      view: 'now',
      siteId: params.siteId,
      shiftLabel: 'Current shift',
      asOfLabel: readiness.asOf || '—',
      stateKey: 'readiness_unavailable',
      stateLabel: block.label,
      message: block.message,
      limitation: [
        block.limitation,
        readiness.missingRequired.length
          ? `Missing: ${readiness.missingRequired.join(', ')}.`
          : '',
        params.dataNote || '',
      ]
        .filter(Boolean)
        .join(' '),
      primaryCta: block.primaryCta,
      readiness,
      nowBlockedByReadiness: true,
      showTrustedNow: false,
      issueLocationLine: null,
      updateModeSummary: null,
      methodologySummary: null,
      diagnostics: formatNowReadinessDiagnostics(readiness),
      overTime: null,
      sinceControl: null,
    };
  }

  if (params.view === 'now' && readiness.mayClaimTrustedNow) {
    const presented = presentTrustedNow({
      readiness,
      focus: params.nowFocus,
      dataNote: params.dataNote,
    });
    return {
      view: 'now',
      siteId: params.siteId,
      shiftLabel: presented.shiftLabel,
      asOfLabel: presented.asOfLabel,
      stateKey: presented.stateKey,
      stateLabel: presented.stateLabel,
      message: presented.message,
      limitation: presented.limitation,
      primaryCta: presented.primaryCta,
      readiness,
      nowBlockedByReadiness: false,
      showTrustedNow: true,
      issueLocationLine: presented.issueLocationLine,
      updateModeSummary: presented.updateModeSummary,
      methodologySummary: presented.methodologySummary,
      diagnostics: presented.diagnostics,
      overTime: null,
      sinceControl: null,
    };
  }

  if (params.view === 'over_time') {
    const overTime = presentOverTime({
      windows: params.overTimeWindows,
      selectedWindowId: params.overTimeWindowId || DEFAULT_OVER_TIME_WINDOW,
      dataNote: params.dataNote,
    });
    return {
      view: 'over_time',
      siteId: params.siteId,
      shiftLabel: overTime.windowLabel,
      asOfLabel: overTime.asOfLabel,
      stateKey: overTime.stateKey,
      stateLabel: overTime.stateLabel,
      message: overTime.message,
      limitation: overTime.limitation,
      primaryCta: overTime.primaryCta,
      readiness,
      nowBlockedByReadiness,
      showTrustedNow: false,
      issueLocationLine: null,
      // update_mode is Now-only — never stamp on over_time.
      updateModeSummary: null,
      methodologySummary: overTime.methodologySummary,
      diagnostics: formatNowReadinessDiagnostics(readiness),
      overTime,
      sinceControl: null,
    };
  }

  // Since control — polished K1 path (F). Never look like live Current / Trusted Now.
  const sinceControl = presentSinceControl({
    window: params.sinceControlWindow,
    dataNote: params.dataNote,
  });
  return {
    view: 'since_control',
    siteId: params.siteId,
    shiftLabel: sinceControl.shiftLabel,
    asOfLabel: sinceControl.asOfLabel,
    stateKey: sinceControl.stateKey,
    stateLabel: sinceControl.stateLabel,
    message: sinceControl.message,
    limitation: sinceControl.limitation,
    primaryCta: sinceControl.primaryCta,
    readiness,
    nowBlockedByReadiness,
    showTrustedNow: false,
    issueLocationLine: null,
    // update_mode is Now-only — never stamp on since_control.
    updateModeSummary: null,
    methodologySummary: sinceControl.methodologySummary,
    diagnostics: formatNowReadinessDiagnostics(readiness),
    overTime: null,
    sinceControl,
  };
}
